import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputNumber, Empty, Checkbox } from 'antd';

import { BLACK } from 'shared/constants/colors';
import { average, percent, round } from 'shared/constants/math';

import { uniqueSegmentId, getSegment } from './utils';
import { formatSegment, axisFormatter } from './formatters';
import Chart, { DEFAULT_CHART_HEIGHT } from './Chart';
import messages from './messages';

import './charts.less';

const labelLayout = (params) => ({
  x: params.rect.x + 20,
  y: params.rect.y,
  draggable: true,
});

const ScatterChart = (props) => {
  const { question, comparedTo, segments, chartSettings, onSelectChartSegment, precision } = props;
  const { formatMessage, locale } = useIntl();
  // NOTE: We can only compare to scores for now.
  // So yAxis can never be shown as percent.
  const showAsPercentage = chartSettings.displayMode === 'percentage';
  const [showLabels, setShowLabels] = useState();
  const [xAxisMarker, setXAxisMarker] = useState(0);
  const [yAxisMarker, setYAxisMarker] = useState(0);

  const xValues = useMemo(() => segments
    .map(segment => segment.values.map(v => (showAsPercentage ? percent(v.value, segment.uniqueCount) : v.value)))
    .flat(), [segments, showAsPercentage]);

  const yValues = useMemo(() => segments
    .map(segment => segment.values.map(v => (v.yValue || 0)))
    .flat(), [segments]);

  const inputStep = 1 / (10 ** precision);

  const isEmpty = xValues.every(val => val === 0) && yValues.every(val => val === 0);

  useEffect(() => {
    const settings = chartSettings || {};
    const xMarker = settings.xAxisMarker != null ? settings.xAxisMarker : average([Math.min(...xValues), Math.max(...xValues)], { precision: 0 });
    const yMarker = settings.yAxisMarker != null ? settings.yAxisMarker : average(yValues, { precision });
    setXAxisMarker(xMarker);
    setYAxisMarker(yMarker);
    setShowLabels(settings.showLabels != null ? settings.showLabels : segments.length === 1);
  }, [xValues, yValues, chartSettings]);

  const formatLabel = useCallback(item => (question.translateNames && messages[item.name]
    ? formatMessage(messages[item.name])
    : item.name), [question.translateNames]);

  const series = useMemo(() => segments.map(segment => {
    const marklineData = [];
    if (xAxisMarker != null) {
      marklineData.push({ xAxis: xAxisMarker });
    }
    if (yAxisMarker != null) {
      marklineData.push({ yAxis: yAxisMarker });
    }
    return {
      type: 'scatter',
      name: uniqueSegmentId(segment),
      data: segment.values.map((val) => {
        const xValue = showAsPercentage
          ? percent(val.value, segment.uniqueCount)
          : val.value;

        return {
          ...val,
          segmentId: segment.segmentId,
          value: [xValue, val.yValue],
        };
      }),
      symbolSize: 13,
      itemStyle: { color: segment.color },
      markLine: marklineData.length ? {
        silent: true,
        symbolSize: 0,
        data: marklineData,
        lineStyle: {
          width: 2,
          type: 'solid',
          color: BLACK,
          opacity: 0.4,
        },
      } : undefined,
      label: {
        show: showLabels,
        color: BLACK,
        formatter: formatLabel,
      },
      labelLine: {
        show: true,
        lineStyle: {
          color: '#bbb',
        },
      },
      labelLayout,
      emphasis: {
        focus: 'self',
        label: {
          show: true,
          color: BLACK,
        },
      },
      blur: {
        itemStyle: {
          opacity: 0.3,
        },
      },
    };
  }), [segments, showLabels, showAsPercentage, formatLabel, xAxisMarker, yAxisMarker]);

  const formatTooltip = useCallback((item) => {
    const { marker, value, name } = item;
    let tooltip = `${marker} ${name}`;
    tooltip += '<br />';
    tooltip += `${question.title}: ${round(value[0], precision)}`;
    if (showAsPercentage) {
      tooltip += '%';
    }
    tooltip += '<br />';
    tooltip += `${comparedTo.title}: ${round(value[1], precision)}`;
    return tooltip;
  }, [question.title, comparedTo, showAsPercentage]);

  const options = useMemo(() => ({
    animation: chartSettings ? chartSettings.animation : true,
    grid: {
      left: 40,
      right: 35,
      top: 50,
      bottom: 35,
      containLabel: true,
    },
    series,
    legend: {
      data: series.map(s => s.name),
      formatter: name => formatSegment(getSegment(segments, name), { formatMessage, locale }),
    },
    xAxis: {
      name: question.title,
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: {
        fontSize: 14,
      },
      axisLabel: { formatter: axisFormatter(showAsPercentage) },
      axisLine: { show: false },
      boundaryGap: ['0', '25%'],
    },
    yAxis: {
      name: comparedTo.title,
      nameLocation: 'middle',
      nameGap: 30,
      nameRotate: 90,
      nameTextStyle: {
        fontSize: 14,
      },
      axisLine: { show: false },
      boundaryGap: ['10%', '10%'],
      scale: true,
      min: ({ min }) => (yAxisMarker < min ? yAxisMarker : null),
      max: ({ max }) => (yAxisMarker > max ? yAxisMarker : null),
    },
    tooltip: {
      trigger: 'item',
      axisPointer: {
        show: true,
        type: 'cross',
        label: { show: false },
      },
      formatter: formatTooltip,
    },
  }), [chartSettings, series, segments, formatTooltip, yAxisMarker, showAsPercentage]);

  const handleSelectChartSegment = useCallback((data) => {
    onSelectChartSegment?.({ data, question });
  }, [onSelectChartSegment, question]);

  const toolbox = useMemo(() => ({
    ...props.toolbox,
    left: (
      <>
        <div>
          <FormattedMessage {...messages.xAxisMarker} />
          <InputNumber
            size="small"
            step={inputStep}
            value={xAxisMarker}
            onChange={setXAxisMarker}
            style={{ marginLeft: 8 }}
          />
        </div>
        <div>
          <FormattedMessage {...messages.yAxisMarker} />
          <InputNumber
            size="small"
            step={inputStep}
            value={yAxisMarker}
            onChange={setYAxisMarker}
            style={{ marginLeft: 8 }}
          />
        </div>
        <Checkbox checked={showLabels} onChange={() => setShowLabels(!showLabels)}><FormattedMessage {...messages.showLabels} /></Checkbox>
      </>
    ),
    addToReport: props.toolbox.addToReport && (() => {
      props.toolbox.addToReport({
        contentSettings: {
          questionIds: question.questionIds,
          comparisonQuestionIds: comparedTo.questionIds,
        },
        chartSettings: {
          xAxisMarker,
          yAxisMarker,
          showLabels,
          displayMode: showAsPercentage ? 'percentage' : 'value',
        },
      });
    }),
  }), [props.toolbox, xAxisMarker, yAxisMarker, showLabels, showAsPercentage]);

  if (isEmpty) {
    return (
      <Empty
        style={{ height: DEFAULT_CHART_HEIGHT }}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<FormattedMessage {...messages.noMatchingData} />}
      />
    );
  }

  return (
    <Chart
      {...props}
      className="scatter"
      alwaysHorizontalLegend
      onSelectChartSegment={handleSelectChartSegment}
      option={options}
      height="auto"
      toolbox={toolbox}
    />
  );
};

export default ScatterChart;
